import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { receiptsAPI, formatApiError, resolveImageUrl } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, DownloadSimple, ShareNetwork, Check, ArrowUpRight, ArrowDownLeft, ShoppingCart, Copy } from '@phosphor-icons/react';

function ReceiptPage() {
  const { transactionId } = useParams();
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const receiptUrl = `${window.location.origin}/receipt/${transactionId}`;

  const loadReceipt = useCallback(async () => {
    try {
      const { data } = await receiptsAPI.get(transactionId);
      setReceipt(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [transactionId]);

  useEffect(() => { loadReceipt(); }, [loadReceipt]);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(receiptUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadPdf = () => {
    window.open(receiptsAPI.getPdfUrl(transactionId), '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="animate-pulse text-[#999999]">Loading receipt...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Card className="bg-[#111111] border-[#222222] max-w-md w-full mx-4">
          <CardContent className="p-8 text-center">
            <div className="text-4xl mb-4">404</div>
            <p className="text-[#999999] mb-6">{error}</p>
            <Link to="/">
              <Button variant="outline" className="border-[#333333] text-white hover:bg-[#222222]">
                Go Home
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { type, amount, fee, fee_payer, note, payment_method, source, created_at, sender, recipient, product } = receipt;

  const typeConfig = {
    send: { label: 'Payment', icon: ArrowUpRight, color: '#22C55E' },
    purchase: { label: 'Purchase', icon: ShoppingCart, color: '#22C55E' },
    topup: { label: 'Top-up', icon: ArrowDownLeft, color: '#F59E0B' },
  };
  const config = typeConfig[type] || typeConfig.send;
  const TypeIcon = config.icon;

  let dateStr = '';
  if (created_at) {
    try {
      const dt = new Date(created_at);
      dateStr = dt.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZoneName: 'short' });
    } catch (err) { console.warn('Receipt date parse failed:', err); dateStr = created_at; }
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] py-8 px-4" data-testid="receipt-page">
      {/* Back link */}
      <div className="max-w-lg mx-auto mb-4">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-[#999999] hover:text-white transition-colors">
          <ArrowLeft className="h-4 w-4" />
          <span>Divispend</span>
        </Link>
      </div>

      {/* Main receipt card */}
      <Card className="max-w-lg mx-auto bg-[#111111] border-[#1A1A1A] overflow-hidden" data-testid="receipt-card">
        {/* Green header bar */}
        <div className="h-1.5 bg-[#22C55E]" />

        <CardContent className="p-0">
          {/* Header section */}
          <div className="px-8 pt-8 pb-6 text-center border-b border-[#1A1A1A]">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full mb-4" style={{ backgroundColor: `${config.color}15` }}>
              <TypeIcon className="h-7 w-7" weight="duotone" style={{ color: config.color }} />
            </div>
            <p className="text-xs tracking-[0.15em] uppercase font-semibold mb-2" style={{ color: config.color }}>
              {config.label}
            </p>
            <p className="text-5xl font-bold text-white tracking-tight" data-testid="receipt-amount">
              ${amount?.toFixed(2)}
            </p>
            {fee > 0 && (
              <p className="text-xs text-[#666666] mt-2">
                + ${fee.toFixed(2)} fee ({fee_payer})
              </p>
            )}
            <div className="inline-flex items-center gap-1.5 mt-3 px-3 py-1 rounded-full bg-[#22C55E]/10">
              <Check className="h-3.5 w-3.5 text-[#22C55E]" weight="bold" />
              <span className="text-xs font-medium text-[#22C55E]">Completed</span>
            </div>
          </div>

          {/* Parties section */}
          <div className="px-8 py-6 border-b border-[#1A1A1A]">
            <div className="flex items-center justify-between">
              {/* Sender */}
              <div className="flex items-center gap-3">
                {sender?.avatar_url ? (
                  <img src={sender.avatar_url} alt="" className="w-10 h-10 rounded-full object-cover" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-[#222222] flex items-center justify-center text-white font-semibold text-sm">
                    {sender?.name?.[0] || '?'}
                  </div>
                )}
                <div>
                  <p className="text-xs text-[#666666]">From</p>
                  <p className="text-sm font-medium text-white" data-testid="receipt-sender">{sender?.name || 'Unknown'}</p>
                  {sender?.username && <p className="text-xs text-[#555555]">${sender.username}</p>}
                </div>
              </div>

              <ArrowUpRight className="h-5 w-5 text-[#333333]" />

              {/* Recipient */}
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <p className="text-xs text-[#666666]">To</p>
                  <p className="text-sm font-medium text-white" data-testid="receipt-recipient">{recipient?.name || 'Unknown'}</p>
                  {recipient?.username && <p className="text-xs text-[#555555]">${recipient.username}</p>}
                </div>
                {recipient?.avatar_url ? (
                  <img src={recipient.avatar_url} alt="" className="w-10 h-10 rounded-full object-cover" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-[#222222] flex items-center justify-center text-white font-semibold text-sm">
                    {recipient?.name?.[0] || '?'}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Product section (for purchases) */}
          {product && (
            <div className="px-8 py-5 border-b border-[#1A1A1A]">
              <p className="text-xs tracking-[0.1em] uppercase font-semibold text-[#666666] mb-3">Product</p>
              <div className="flex items-center gap-3">
                {product.image_url && (
                  <img src={resolveImageUrl(product.image_url)} alt="" className="w-12 h-12 rounded-lg object-cover border border-[#222222]" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">{product.name}</p>
                  {product.category && <p className="text-xs text-[#555555]">{product.category}</p>}
                </div>
                <p className="text-sm font-semibold text-[#22C55E]">${product.price?.toFixed(2)}</p>
              </div>
            </div>
          )}

          {/* Details section */}
          <div className="px-8 py-5 border-b border-[#1A1A1A]">
            <p className="text-xs tracking-[0.1em] uppercase font-semibold text-[#666666] mb-3">Details</p>
            <div className="space-y-3">
              <DetailRow label="Transaction ID" value={transactionId} mono data-testid="receipt-txn-id" />
              <DetailRow label="Date" value={dateStr} />
              {payment_method && <DetailRow label="Method" value={payment_method.charAt(0).toUpperCase() + payment_method.slice(1)} />}
              {source && <DetailRow label="Source" value={source === 'pay_link' ? 'Payment Link' : source} />}
              {note && <DetailRow label="Note" value={note} />}
            </div>
          </div>

          {/* QR Code section */}
          <div className="px-8 py-6 border-b border-[#1A1A1A] flex flex-col items-center">
            <p className="text-xs tracking-[0.1em] uppercase font-semibold text-[#666666] mb-4">Verification QR</p>
            <div className="bg-white p-3 rounded-xl">
              <QRCodeSVG
                value={receiptUrl}
                size={120}
                fgColor="#111111"
                bgColor="#FFFFFF"
                level="M"
              />
            </div>
            <p className="text-[10px] text-[#444444] mt-2">Scan to verify this receipt</p>
          </div>

          {/* Action buttons */}
          <div className="px-8 py-6 flex gap-3">
            <Button
              onClick={handleCopyLink}
              variant="outline"
              className="flex-1 h-11 border-[#222222] text-white hover:bg-[#1A1A1A] rounded-lg text-sm"
              data-testid="receipt-copy-link"
            >
              {copied ? <Check className="h-4 w-4 mr-2 text-[#22C55E]" /> : <Copy className="h-4 w-4 mr-2" />}
              {copied ? 'Copied' : 'Copy Link'}
            </Button>
            <Button
              onClick={handleDownloadPdf}
              className="flex-1 h-11 bg-[#22C55E] hover:bg-[#1EA34B] text-black font-semibold rounded-lg text-sm"
              data-testid="receipt-download-pdf"
            >
              <DownloadSimple className="h-4 w-4 mr-2" />
              Download PDF
            </Button>
          </div>

          {/* Footer */}
          <div className="px-8 pb-6 text-center">
            <p className="text-[10px] text-[#333333]">
              Powered by Divispend · Send. Receive. Grow.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function DetailRow({ label, value, mono }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <span className="text-xs text-[#555555] shrink-0">{label}</span>
      <span className={`text-xs text-white text-right ${mono ? 'font-mono' : ''} break-all`}>{value}</span>
    </div>
  );
}

export default ReceiptPage;
